import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, ShoppingBag, Compass, Package } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/context/LanguageContext";

const FloatingCart = () => {
  const { isCartOpen, toggleCart, cartItems, removeFromCart, updateQuantity, cartTotal } = useCart();
  const navigate = useNavigate();
  const { t } = useLanguage();

  const handleCheckout = () => {
    toggleCart();
    navigate("/checkout");
  };

  const handleTrackOrder = () => {
    toggleCart();
    navigate("/");
    setTimeout(() => {
      const trackSection = document.getElementById("track-order");
      if (trackSection) trackSection.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  return (
    <AnimatePresence>
      {isCartOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={toggleCart} className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50" />
          <motion.div
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 h-full w-full max-w-md bg-card border-l border-border z-50 shadow-elegant rtl:right-auto rtl:left-0 rtl:border-l-0 rtl:border-r rtl:border-border"
          >
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-between p-6 border-b border-border">
                <div className="flex items-center gap-3">
                  <ShoppingBag className="w-6 h-6 text-primary" />
                  <h2 className="text-xl font-serif">{t.cart.title}</h2>
                </div>
                <button onClick={toggleCart} className="p-2 hover:bg-muted rounded-sm transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6">
                {cartItems.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full">
                    <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.2 }} className="text-center space-y-4">
                      <ShoppingBag className="w-16 h-16 text-muted-foreground/30 mx-auto" />
                      <div>
                        <p className="text-foreground font-medium">{t.cart.empty}</p>
                        <p className="text-sm text-muted-foreground mt-1">{t.cart.emptyDesc}</p>
                      </div>
                      <div className="space-y-3 pt-6">
                        <Button variant="bronze" size="lg" className="w-full" onClick={() => { toggleCart(); navigate("/collection"); }}>
                          <Compass className="w-4 h-4 mr-2" />{t.cart.browseProducts}
                        </Button>
                        <Button variant="outline" size="lg" className="w-full" onClick={handleTrackOrder}>
                          <Package className="w-4 h-4 mr-2" />{t.cart.trackOrder}
                        </Button>
                      </div>
                    </motion.div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {cartItems.map((item) => (
                      <motion.div key={`${item.product.id}-${item.selectedSize}`} layout initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, x: 100 }} className="flex gap-4 p-4 bg-muted/30 rounded-sm">
                        <img src={item.product.image} alt={item.product.name} className="w-20 h-24 object-cover rounded-sm" />
                        <div className="flex-1">
                          <h3 className="font-serif text-lg">{item.product.name}</h3>
                          <p className="text-sm text-muted-foreground">{item.selectedSize}</p>
                          <p className="text-primary font-medium mt-1">{item.price} {t.common.egp}</p>
                          <div className="flex items-center gap-3 mt-3">
                            <button onClick={() => updateQuantity(item.product.id, item.selectedSize, item.quantity - 1)} className="w-8 h-8 flex items-center justify-center border border-border rounded-sm hover:bg-muted transition-colors">
                              <Minus className="w-4 h-4" />
                            </button>
                            <span className="w-8 text-center">{item.quantity}</span>
                            <button onClick={() => updateQuantity(item.product.id, item.selectedSize, item.quantity + 1)} className="w-8 h-8 flex items-center justify-center border border-border rounded-sm hover:bg-muted transition-colors">
                              <Plus className="w-4 h-4" />
                            </button>
                            <button onClick={() => removeFromCart(item.product.id, item.selectedSize)} className="ml-auto text-muted-foreground hover:text-destructive transition-colors">
                              <X className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>

              {cartItems.length > 0 && (
                <div className="border-t border-border p-6 space-y-4">
                  <div className="flex items-center justify-between text-lg">
                    <span className="text-muted-foreground">{t.cart.subtotal}</span>
                    <span className="font-serif text-xl">{cartTotal.toFixed(2)} {t.common.egp}</span>
                  </div>
                  <Button variant="bronze" size="lg" className="w-full" onClick={handleCheckout}>{t.cart.checkout}</Button>
                  <p className="text-xs text-center text-muted-foreground">{t.cart.shippingNote}</p>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default FloatingCart;
